import React, { useState, useEffect } from 'react';
import { BED_CONFIGS, WOOD_TYPES, FINISHES, GUARDRAIL_CONFIGS, ENTRY_TYPES, CORNER_POSTS_OPTIONS, ADDONS } from '../config/bedConfigs';
import { generateOrderNumber } from '../utils/calculations';
import { saveOrder } from '../utils/storage';

export default function OrderEntryForm({ onSave, initialOrder = null }) {
  const [formData, setFormData] = useState({
    orderNumber: '',
    orderDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    customerName: '',
    customerInitials: '',
    deliveryCity: '',
    deliveryState: '',
    deliveryZip: '',
    bedConfig: 'Twin/Twin',
    woodType: 'Select Pine',
    finish: 'Painted White',
    guardrailConfig: 'L-Shape',
    entryType: 'Ladder',
    cornerPosts: 0,
    addons: [],
    salesRepName: '',
    closeDate: '',
    actualDeliveryCost: '',
    actualInstallationCost: '',
    status: {
      cuttingComplete: false,
      edgeBandingComplete: false,
      holesDrilledComplete: false,
      readyForPaint: false,
      paintComplete: false,
      qcPassed: false,
      packedReadyToShip: false,
      problemFlagged: false,
      problemNotes: '',
    },
  });

  useEffect(() => {
    if (initialOrder) {
      setFormData(initialOrder);
    } else {
      setFormData(prev => ({
        ...prev,
        orderNumber: generateOrderNumber(),
      }));
    }
  }, [initialOrder]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && name.startsWith('addon_')) {
      const addonName = name.replace('addon_', '');
      setFormData(prev => ({
        ...prev,
        addons: checked
          ? [...prev.addons, addonName]
          : prev.addons.filter(a => a !== addonName),
      }));
    } else if (name.startsWith('status.')) {
      const statusField = name.replace('status.', '');
      setFormData(prev => ({
        ...prev,
        status: {
          ...prev.status,
          [statusField]: type === 'checkbox' ? checked : value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const orderToSave = {
      ...formData,
      actualDeliveryCost: parseFloat(formData.actualDeliveryCost) || 0,
      actualInstallationCost: parseFloat(formData.actualInstallationCost) || 0,
    };
    saveOrder(orderToSave);
    if (onSave) onSave(orderToSave);
    alert('Order saved successfully!');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-light text-white mb-1">
          {initialOrder ? 'Edit Order' : 'New Order'}
        </h2>
        <p className="text-gray-light text-sm">Fill in the order details below</p>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Order Information */}
        <div className="card p-6">
          <h3 className="card-title mb-6 flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-warm-gold/20 flex items-center justify-center text-xs text-warm-gold font-bold">1</span>
            Order Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="info-label">Order Number</label>
              <input
                type="text"
                name="orderNumber"
                value={formData.orderNumber}
                readOnly
                className="input-styled bg-charcoal-light/50 cursor-not-allowed"
              />
            </div>
            
            <div>
              <label className="info-label">Order Date</label>
              <input
                type="date"
                name="orderDate"
                value={formData.orderDate}
                onChange={handleChange}
                className="input-styled"
                required
              />
            </div>
            
            <div>
              <label className="info-label">Due Date</label>
              <input
                type="date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="input-styled"
              />
            </div>
            
            <div>
              <label className="info-label">Sales Rep Name</label>
              <input
                type="text"
                name="salesRepName"
                value={formData.salesRepName}
                onChange={handleChange}
                placeholder="Enter sales rep name"
                className="input-styled"
              />
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="card p-6">
          <h3 className="card-title mb-6 flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-warm-gold/20 flex items-center justify-center text-xs text-warm-gold font-bold">2</span>
            Customer Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="info-label">Customer Name</label>
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                placeholder="John Smith"
                className="input-styled"
              />
            </div>
            
            <div>
              <label className="info-label">Customer Initials</label>
              <input
                type="text"
                name="customerInitials"
                value={formData.customerInitials}
                onChange={handleChange}
                placeholder="JS"
                className="input-styled"
              />
            </div>
            
            <div>
              <label className="info-label">Delivery City</label>
              <input
                type="text"
                name="deliveryCity"
                value={formData.deliveryCity}
                onChange={handleChange}
                placeholder="Santa Rosa Beach"
                className="input-styled"
              />
            </div>
            
            <div>
              <label className="info-label">Delivery State</label>
              <input
                type="text"
                name="deliveryState"
                value={formData.deliveryState}
                onChange={handleChange}
                placeholder="FL"
                className="input-styled"
              />
            </div>
            
            <div>
              <label className="info-label">Delivery ZIP</label>
              <input
                type="text"
                name="deliveryZip"
                value={formData.deliveryZip}
                onChange={handleChange}
                placeholder="32459"
                className="input-styled"
              />
            </div>
          </div>
        </div>

        {/* Bed Configuration */}
        <div className="card p-6">
          <h3 className="card-title mb-6 flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-warm-gold/20 flex items-center justify-center text-xs text-warm-gold font-bold">3</span>
            Bed Configuration
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="info-label">Bed Configuration</label>
              <select
                name="bedConfig"
                value={formData.bedConfig}
                onChange={handleChange}
                className="select-styled"
              >
                {Object.keys(BED_CONFIGS).map(config => (
                  <option key={config} value={config}>{config}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="info-label">Wood Type</label>
              <select
                name="woodType"
                value={formData.woodType}
                onChange={handleChange}
                className="select-styled"
              >
                {Object.keys(WOOD_TYPES).map(wood => (
                  <option key={wood} value={wood}>{wood}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="info-label">Finish</label>
              <select
                name="finish"
                value={formData.finish}
                onChange={handleChange}
                className="select-styled"
              >
                {FINISHES.map(finish => (
                  <option key={finish} value={finish}>{finish}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="info-label">Guardrail Configuration</label>
              <select
                name="guardrailConfig"
                value={formData.guardrailConfig}
                onChange={handleChange}
                className="select-styled"
              >
                {GUARDRAIL_CONFIGS.map(config => (
                  <option key={config} value={config}>{config}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="info-label">Entry Type</label>
              <select
                name="entryType"
                value={formData.entryType}
                onChange={handleChange}
                className="select-styled"
              >
                {ENTRY_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="info-label">Corner Posts</label>
              <select
                name="cornerPosts"
                value={formData.cornerPosts}
                onChange={handleChange}
                className="select-styled"
              >
                {CORNER_POSTS_OPTIONS.map(num => (
                  <option key={num} value={num}>{num}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Add-ons */}
        <div className="card p-6">
          <h3 className="card-title mb-6 flex items-center gap-3">
            <span className="w-6 h-6 rounded-full bg-warm-gold/20 flex items-center justify-center text-xs text-warm-gold font-bold">4</span>
            Premium Add-ons
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {ADDONS.map(addon => {
              const isSelected = formData.addons.includes(addon);
              return (
                <label 
                  key={addon} 
                  className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                    isSelected 
                      ? 'bg-warm-gold/10 border-warm-gold' 
                      : 'bg-charcoal-light border-white/10 hover:border-warm-gold/50'
                  }`}
                >
                  <input
                    type="checkbox"
                    name={`addon_${addon}`}
                    checked={isSelected}
                    onChange={handleChange}
                    className="w-5 h-5 rounded border-2 border-white/20 bg-transparent checked:bg-warm-gold checked:border-warm-gold focus:ring-warm-gold focus:ring-offset-0"
                    style={{ accentColor: '#c9a959' }}
                  />
                  <span className="text-sm text-white">{addon}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="submit"
            className="btn-gold px-8 py-4 text-sm flex items-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            Save Order
          </button>
        </div>
      </form>
    </div>
  );
}
